import type React from "react"
import { useCallback } from "react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

interface CurrencyInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  prefixClassName?: string
  disabled?: boolean
  onBlur?: () => void
}

function sanitize(raw: string): string {
  // Allow digits, one decimal point, and leading minus
  let result = ""
  let hasDot = false
  let fractionDigits = 0
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]
    if (ch === "-" && i === 0) {
      result += ch
    } else if (ch === "." && !hasDot) {
      hasDot = true
      result += ch
    } else if (ch >= "0" && ch <= "9") {
      if (!hasDot || fractionDigits < 2) {
        result += ch
        if (hasDot) {
          fractionDigits += 1
        }
      }
    }
  }
  return result
}

function formatToTwoDecimals(val: string): string {
  if (!val || val === "-") return val
  const num = parseFloat(val)
  if (isNaN(num)) return ""
  return num.toFixed(2)
}

function CurrencyInput({
  value,
  onChange,
  placeholder = "0.00",
  className,
  prefixClassName,
  disabled,
  onBlur,
}: CurrencyInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(sanitize(e.target.value))
    },
    [onChange]
  )

  const handleBlur = useCallback(() => {
    if (value) {
      onChange(formatToTwoDecimals(value))
    }
    onBlur?.()
  }, [value, onChange, onBlur])

  return (
    <div className="relative">
      <span
        className={cn(
          "pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-foreground",
          prefixClassName
        )}
      >
        $
      </span>
      <Input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn("pl-7", className)}
      />
    </div>
  )
}

export { CurrencyInput }
export type { CurrencyInputProps }
